/**
 * Attribution Tracking System
 * Captures marketing attribution parameters and saves them to cart for order attribution
 *
 * Tracks:
 * - Google Ads (gclid)
 * - Facebook/Instagram (fbclid)
 * - TikTok (ttclid)
 * - Microsoft Ads (msclkid)
 * - UTM Parameters (utm_source, utm_medium, utm_campaign, utm_content, utm_term)
 * - Referrer
 *
 * @version 1.0.0
 * @date 2025-11-04
 */

(function () {
  'use strict';

  // Click IDs are opaque platform tokens (URL-safe base64 plus padding). Reject
  // anything outside that charset so an attacker-controlled param value can
  // never reach cart.note or cart.attributes. Kept byte-for-byte in sync with
  // sanitizeClickId in snippets/click-id-early-capture.liquid.
  function sanitizeClickId(v) {
    return v && /^[A-Za-z0-9._~=+-]{1,512}$/.test(v) ? v : null;
  }

  // Collect every top-level occurrence of a param, in URL order. Mirrors
  // findAllTopLevelValues in snippets/click-id-early-capture.liquid.
  function findAllTopLevelValues(params, name) {
    const out = [];
    try {
      const parsed = params.getAll(name);
      for (let p = 0; p < parsed.length; p++) {
        if (parsed[p]) out.push(parsed[p]);
      }
    } catch (_e) {
      // getAll unavailable on very old browsers; fall back to a raw scan of search.
      const search = window.location.search || '';
      const all = search.match(new RegExp('[?&]' + name + '=([^&#]*)', 'g')) || [];
      for (let j = 0; j < all.length; j++) {
        const v = all[j].slice(all[j].indexOf('=') + 1);
        if (v) out.push(v);
      }
    }
    return out;
  }

  // Some entry flows nest the real destination inside a param VALUE (e.g. an
  // SSO return_to redirect), where URLSearchParams cannot see the click ID at
  // all. Mirrors findParamAnywhere in snippets/click-id-early-capture.liquid.
  function findParamAnywhere(name) {
    const haystacks = [window.location.href];
    try {
      const decoded = decodeURIComponent(window.location.href);
      if (decoded !== haystacks[0]) haystacks.push(decoded);
    } catch (_e) {
      // Malformed percent-escape; the raw scan below still applies.
    }
    for (let i = 0; i < haystacks.length; i++) {
      const m = haystacks[i].match(new RegExp('[?&]' + name + '=([^&#]*)'));
      if (m && m[1]) return m[1];
    }
    return null;
  }

  // Resolution order, narrowest first:
  //   1. Every TOP-LEVEL occurrence of the param, in URL order — first VALID
  //      value wins. This is what recovers a real click ID that follows an
  //      unsubstituted macro placeholder such as Pinterest's ?epik={epik}.
  //   2. Only when the param is absent at top level entirely, the nested scan.
  // Mirrors readClickId in snippets/click-id-early-capture.liquid; keep the
  // two in sync rather than letting a third variant of this logic drift in.
  function readClickId(params, name) {
    const topLevel = findAllTopLevelValues(params, name);
    for (let c = 0; c < topLevel.length; c++) {
      const valid = sanitizeClickId(topLevel[c]);
      if (valid) return valid;
    }
    if (topLevel.length > 0) return null;
    return sanitizeClickId(findParamAnywhere(name));
  }

  // Configuration
  const CONFIG = {
    storageKey: 'coracrea_attribution',
    quizStorageKey: 'coracrea_quiz_attribution',
    visitStampKey: 'attribution_visit_stamp',
    expiryDays: 30, // Attribution window
    debug: false, // Set to true for console logging
  };

  /**
   * Main tracking object
   */
  const AttributionTracker = {
    _listeners: {
      domContentLoaded: null,
      cartUpdated: null,
    },
    /**
     * Initialize tracking on page load
     */
    init() {
      try {
        this.log('Attribution Tracker initialized');

        // Capture current parameters
        const attribution = this.captureAttribution();

        // Save to storage if we have new data
        if (attribution && Object.keys(attribution).length > 0) {
          this.saveAttribution(attribution);
          this.log('New attribution captured:', attribution);
        }

        // Update cart attributes
        this.updateCartAttributes();
      } catch (error) {
        this.error('Initialization error:', error);
      }
    },

    /**
     * Capture attribution from current URL and referrer
     */
    captureAttribution() {
      const params = new URLSearchParams(window.location.search);
      const attribution = {};

      // Click IDs outrank utm and referrer. A macro placeholder such as ?epik={epik}&epik=<real> shadows the real id under params.get(), so readClickId takes the first VALID occurrence, as snippets/click-id-early-capture.liquid::readClickId does (memory/ads/pinterest-epik-monitor-actions.json). Rank and label come from the one list in the early-capture snippet (window.CoraClickIdPriority); the first entry with a resolved id labels the visit. Every Google id on the URL is part of this visit's tuple, because the shared merge rule (window.CoraAttributionRule.mergeVisit) replaces the whole tuple; other kinds store their first resolved id.
      const priority = window.CoraClickIdPriority || [];
      if (priority.length === 0) {
        this.log('Click ID priority list missing; click IDs left unlabeled');
      }
      for (const entry of priority) {
        const found = entry.params.filter((param) => readClickId(params, param));
        if (found.length === 0) continue;
        const isGoogle = found[0] === 'gclid' || found[0] === 'gbraid' || found[0] === 'wbraid';
        for (const param of isGoogle ? found : [found[0]]) {
          attribution['_' + param.replace(/^_/, '')] = readClickId(params, param);
        }
        attribution._source = entry.source;
        attribution._medium = entry.medium;
        break;
      }

      // UTM Parameters (used if no click ID)
      if (params.has('utm_source')) {
        if (!attribution._source) attribution._source = params.get('utm_source');
        attribution._utm_source = params.get('utm_source');

        // Special handling for gift quiz
        if (params.get('utm_source') === 'gift-quiz') {
          attribution._quiz_source = 'Gift-quiz';
        }
      }
      if (params.has('utm_medium')) {
        if (!attribution._medium) attribution._medium = params.get('utm_medium');
        attribution._utm_medium = params.get('utm_medium');
      }
      if (params.has('utm_campaign')) {
        attribution._campaign = params.get('utm_campaign');
        attribution._utm_campaign = params.get('utm_campaign');
      }
      if (params.has('utm_content')) {
        attribution._utm_content = params.get('utm_content');
      }
      if (params.has('utm_term')) {
        attribution._utm_term = params.get('utm_term');
      }

      // Capture referrer (only if no click ID or UTM)
      if (document.referrer && !attribution._source) {
        try {
          const referrerUrl = new URL(document.referrer);
          const referrerHost = referrerUrl.hostname.toLowerCase();

          // Only capture external referrers (not same domain)
          if (!referrerHost.includes(window.location.hostname)) {
            attribution._referrer = referrerHost;

            // Identify common referrers
            if (referrerHost.includes('google')) {
              attribution._source = 'Google';
              attribution._medium = 'organic';
            } else if (referrerHost.includes('facebook') || referrerHost.includes('fb.com')) {
              attribution._source = 'Facebook';
              attribution._medium = 'social';
            } else if (referrerHost.includes('instagram')) {
              attribution._source = 'Instagram';
              attribution._medium = 'social';
            } else if (referrerHost.includes('pinterest')) {
              attribution._source = 'Pinterest';
              attribution._medium = 'social';
            } else if (referrerHost.includes('tiktok')) {
              attribution._source = 'TikTok';
              attribution._medium = 'social';
            } else if (referrerHost.includes('twitter') || referrerHost.includes('t.co')) {
              attribution._source = 'Twitter';
              attribution._medium = 'social';
            } else if (referrerHost.includes('linkedin')) {
              attribution._source = 'LinkedIn';
              attribution._medium = 'social';
            } else if (referrerHost.includes('bing') || referrerHost.includes('yahoo')) {
              attribution._source = 'Search Engine';
              attribution._medium = 'organic';
            }
          }
        } catch (e) {
          this.log('Error parsing referrer:', e);
        }
      }

      // `_timestamp` dates THIS visit and is rewritten on every attributed visit, so it is never the click time. The Google tuple and its `_click_timestamp` belong to CoraAttributionRule.mergeVisit: a non-Google visit never changes them and it dates a new tuple itself.
      if (Object.keys(attribution).length > 0) {
        attribution._timestamp = this.visitStamp();
      }

      return attribution;
    },

    // Time this browser session first stamped an attributed visit (mirrors visitStamp in click-id-early-capture.liquid).
    visitStamp() {
      // One stamp per browser session, so a reload keeps it and the cart write finds nothing changed (FU-V13e).
      let stamp = '';
      try {
        stamp = sessionStorage.getItem(CONFIG.visitStampKey) || '';
        if (!stamp) {
          stamp = new Date().toISOString();
          sessionStorage.setItem(CONFIG.visitStampKey, stamp);
        }
      } catch (error) {
        this.log('Visit stamp storage unavailable:', error);
      }
      return stamp || new Date().toISOString();
    },

    /**
     * Save attribution to localStorage
     */
    saveAttribution(attribution) {
      try {
        const existing = this.getStoredAttribution();

        // The one merge rule is published by the early-capture snippet: the Google tuple moves as a unit, the label follows the last touch.
        const merged = window.CoraAttributionRule
          ? window.CoraAttributionRule.mergeVisit(existing, attribution)
          : { ...existing, ...attribution };

        // Add expiry
        merged._expiry = Date.now() + CONFIG.expiryDays * 24 * 60 * 60 * 1000;

        localStorage.setItem(CONFIG.storageKey, JSON.stringify(merged));
        this.log('Attribution saved to localStorage');
      } catch (error) {
        this.error('Storage error:', error);
      }
    },

    /**
     * Get stored attribution from localStorage
     */
    getStoredAttribution() {
      try {
        const stored = localStorage.getItem(CONFIG.storageKey);
        if (!stored) return {};

        const attribution = JSON.parse(stored);

        // Check expiry
        if (attribution._expiry && Date.now() > attribution._expiry) {
          this.log('Attribution expired, clearing');
          localStorage.removeItem(CONFIG.storageKey);
          return {};
        }

        return attribution;
      } catch (error) {
        this.error('Storage read error:', error);
        return {};
      }
    },

    /**
     * Get quiz attribution from localStorage
     */
    getQuizAttribution() {
      try {
        const stored = localStorage.getItem(CONFIG.quizStorageKey);
        if (!stored) return {};

        const quizAttribution = JSON.parse(stored);
        this.log('Quiz attribution found:', quizAttribution);
        return quizAttribution;
      } catch (error) {
        this.error('Quiz attribution read error:', error);
        return {};
      }
    },

    /**
     * Clear quiz attribution after it's been used
     */
    clearQuizAttribution() {
      try {
        localStorage.removeItem(CONFIG.quizStorageKey);
        this.log('Quiz attribution cleared');
      } catch (error) {
        this.log('Error clearing quiz attribution:', error);
      }
    },

    /**
     * Update cart attributes with attribution data
     */
    async updateCartAttributes() {
      try {
        const attribution = this.getStoredAttribution();
        const quizAttribution = this.getQuizAttribution();

        // Merge both attributions
        const mergedAttribution = { ...attribution, ...quizAttribution };

        if (!mergedAttribution || Object.keys(mergedAttribution).length === 0) {
          this.log('No attribution to update cart with');
          return;
        }

        // Build cart attributes object — exclude internal expiry field
        const cartAttributes = {};
        // Empty values are skipped: Shopify drops an attribute posted as an empty string, so re-posting one only churns the cart.
        Object.keys(mergedAttribution).forEach((key) => {
          if (!key.startsWith('_expiry') && mergedAttribution[key] !== '') {
            cartAttributes[key] = mergedAttribution[key];
          }
        });

        // The shared writer (snippets/cart-attribute-sync.liquid) serializes cart writes and posts only changed keys
        const response = await window.CoraCartAttributes.write(cartAttributes);

        if (response.ok) {
          this.log('Cart attributes updated successfully');
          // Clear quiz attribution after successful cart update
          if (Object.keys(quizAttribution).length > 0) {
            this.clearQuizAttribution();
          }
        } else {
          this.log('Cart update failed:', response.status);
        }
      } catch (error) {
        // Silently fail - cart update is not critical
        this.log('Cart update error:', error);
      }
    },

    /**
     * Debug logging
     */
    log(...args) {
      if (CONFIG.debug) {
        // eslint-disable-next-line no-console
        console.log('[Attribution Tracker]', ...args);
      }
    },

    error(...args) {
      if (CONFIG.debug) {
        console.error('[Attribution Tracker]', ...args);
      }
    },

    /**
     * Clear attribution (for testing)
     */
    clear() {
      localStorage.removeItem(CONFIG.storageKey);
      this.log('Attribution cleared');
    },

    /**
     * Get current attribution (for debugging)
     */
    getAttribution() {
      return this.getStoredAttribution();
    },

    setupListeners() {
      if (document.readyState === 'loading') {
        this._listeners.domContentLoaded = () => this.init();
        document.addEventListener('DOMContentLoaded', this._listeners.domContentLoaded);
      } else {
        this.init();
      }
      this._listeners.cartUpdated = () => {
        this.log('Cart updated event detected');
        this.updateCartAttributes();
      };
      document.addEventListener('cart:updated', this._listeners.cartUpdated);
    },

    cleanup() {
      if (this._listeners.domContentLoaded) {
        document.removeEventListener('DOMContentLoaded', this._listeners.domContentLoaded);
        this._listeners.domContentLoaded = null;
      }
      if (this._listeners.cartUpdated) {
        document.removeEventListener('cart:updated', this._listeners.cartUpdated);
        this._listeners.cartUpdated = null;
      }
      this.log('Event listeners cleaned up');
    },
  };

  // Initialize on page load with proper listener setup
  AttributionTracker.setupListeners();

  // Cleanup listeners on page unload to prevent memory leaks
  window.addEventListener('beforeunload', () => {
    AttributionTracker.cleanup();
  });

  // Expose to window for debugging
  window.AttributionTracker = AttributionTracker;
})();
